import { useEffect } from "react";
import { absoluteBrandLogoUrl } from "./brand";

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://s-ukdrivingschool.co.uk").replace(/\/$/, "");
export const DEFAULT_OG_IMAGE =
  "https://images.pexels.com/photos/6817037/pexels-photo-6817037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
export const GOOGLE_BUSINESS_URL = "https://www.google.co.uk/search?kgmid=/g/11ntmqry0t&hl=en-GB&q=S-UK+Driving+School";

export const BUSINESS = {
  name: "S-UK Driving School",
  phone: "+447956120510",
  displayPhone: "07956 120 510",
  email: "info@s-ukdrivingschool.co.uk",
  address: "Northolt, UB5",
  serviceAreas: ["Northolt", "Greenford", "Southall", "South Harrow"],
};

export const LOCATION_PATHS = {
  northolt: "/driving-school-northolt",
  greenford: "/driving-school-greenford",
  southall: "/driving-school-southall",
  "south-harrow": "/driving-school-south-harrow",
};

export const locationPath = (slug) => LOCATION_PATHS[slug] || `/driving-school-${slug}`;
export const absoluteUrl = (path = "/") => `${SITE_URL}${path === "/" ? "/" : path.startsWith("/") ? path : `/${path}`}`;
const SEO_SCRIPT_SELECTOR = 'script[data-seo-jsonld="true"], script[data-seo-prerender="true"]';

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: BUSINESS.name,
    alternateName: "S UK Driving School",
    url: SITE_URL,
    logo: absoluteBrandLogoUrl(SITE_URL),
    image: DEFAULT_OG_IMAGE,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    hasMap: GOOGLE_BUSINESS_URL,
    priceRange: "££",
    description: "Manual and automatic driving lessons with 37+ years of experience and a 98% pass rate.",
    paymentAccepted: "Bank transfer, Cash",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Northolt",
      postalCode: "UB5",
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "51.5471",
      longitude: "-0.3674",
    },
    areaServed: BUSINESS.serviceAreas.map((name) => ({ "@type": "Place", name })),
    contactPoint: {
      "@type": "ContactPoint",
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      contactType: "customer service",
      availableLanguage: ["English", "Hindi", "Punjabi", "Urdu"],
    },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "10:00",
      closes: "14:00",
    }],
    sameAs: [
      GOOGLE_BUSINESS_URL,
      "https://www.instagram.com/suk._.driving/",
      "https://www.youtube.com/@sukdrivingschool7462",
      "https://www.facebook.com/sukdrivingschool/",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Driving lesson services",
      itemListElement: ["Automatic driving lessons", "Manual driving lessons", "Refresher driving lessons"].map((name) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name },
      })),
    },
  };
}

function setMeta(attr, key, content) {
  let element = document.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(canonical) {
  let element = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.setAttribute("href", canonical);
}

function setLanguageAlternate(canonical, indexable) {
  let element = document.querySelector('link[rel="alternate"][hreflang="en-GB"]');
  if (!canonical || !indexable) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("link");
    element.rel = "alternate";
    element.hreflang = "en-GB";
    document.head.appendChild(element);
  }
  element.href = canonical;
}

/** Updates route-level metadata after navigation in the client-rendered app. */
export function useSeo({
  title,
  description,
  canonical,
  keywords,
  ogImage,
  ogImageAlt = "S-UK Driving School manual and automatic driving lesson in Northolt",
  ogType = "website",
  noindex = false,
  robots,
  jsonLd,
}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    const robotsValue = robots || (noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setMeta("name", "robots", robotsValue);
    setCanonical(canonical);
    setLanguageAlternate(canonical, !/\bnoindex\b/i.test(robotsValue));
    document.querySelectorAll('meta[name="geo.region"], meta[name="geo.placename"]').forEach((node) => node.remove());

    const image = ogImage || DEFAULT_OG_IMAGE;
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonical || `${SITE_URL}/`);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:alt", ogImageAlt);
    setMeta("property", "og:image:width", "940");
    setMeta("property", "og:image:height", "650");
    setMeta("property", "og:site_name", BUSINESS.name);
    setMeta("property", "og:locale", "en_GB");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:image:alt", ogImageAlt);

    document.querySelectorAll(SEO_SCRIPT_SELECTOR).forEach((node) => node.remove());
    if (jsonLdKey) {
      const parsed = JSON.parse(jsonLdKey);
      (Array.isArray(parsed) ? parsed : [parsed]).forEach((schema) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => document.querySelectorAll(SEO_SCRIPT_SELECTOR).forEach((node) => node.remove());
  }, [title, description, canonical, keywords, ogImage, ogImageAlt, ogType, noindex, robots, jsonLdKey]);
}
